import { useTranslations } from 'next-intl'
import styled from 'styled-components'

import { Box, ContentText, fontSizes, px2rem, spacing } from '@fortum/elemental-ui'

import { getDefaultStartDateByPriceType } from '@/open-web/services/calculators/utils/startDateUtils'
import { sumPricesByType } from '@/open-web/services/calculators/utils/utils'
import { Divider } from '@/shared/components/Divider'
import { discountPercentToString, getEnergyPriceDetails } from '@/shared/components/Price/utils'
import { usePriceToString } from '@/shared/hooks/usePriceToStringFormatter'
import { useTheme } from '@/shared/hooks/useTheme'
import type { EnrichedContractTemplate } from '@/shared/services/campaignDataResolver'
import { useDeliveryInfo } from '@/shared/utils/gtm/gtmHelper'
import {
  SEASON_ENERGY_PRICE_ELEMENTS,
  TARIFF_ELEMENT_TYPE_PREFIX,
  isEnergyElement,
  isStartupCostElement,
} from '@/shared/utils/tariffElementUtils'

import { useContractEnergyPriceExtraText } from '../../hooks/useContractEnergyPriceExtraText'
import {
  calculateFinnishMonthlySavings,
  getEnergyPriceElements,
  getMonthlyFeeElements,
  getStartupConstToDisplay,
} from '../../utils'
import { MonthlyFeeRows } from '../MonthlyFeeRows'
import { MonthlySavings } from '../MonthlySavings'
import { TotalPriceRow } from '../TotalPriceRow'

export type PriceDetailsProps = {
  contractTemplate: EnrichedContractTemplate
  addonsTariffNos: number[]
}

export const FinnishPriceDetails = ({ contractTemplate, addonsTariffNos }: PriceDetailsProps) => {
  const { deliveryArea = 'FI' } = useDeliveryInfo()
  const startDate = getDefaultStartDateByPriceType(contractTemplate.priceType)

  const theme = useTheme()
  const { colors } = theme
  const t = useTranslations('purchaseFlow')
  const tAriaLabels = useTranslations('ariaLabels')
  const { formatPrice, formatPriceWithUnit, formatPriceWithUnitNoAmount } = usePriceToString()

  const energyElements = getEnergyPriceElements(
    contractTemplate,
    addonsTariffNos,
    startDate,
    deliveryArea,
  )

  const startupCostTariffElement = contractTemplate.tariffElements.find((element) =>
    isStartupCostElement(element.type),
  )

  const startupCostToDisplay = startupCostTariffElement
    ? getStartupConstToDisplay(
        startupCostTariffElement,
        contractTemplate.customerType,
        formatPriceWithUnitNoAmount,
      )
    : undefined

  const monthlyFeeElements = getMonthlyFeeElements(
    contractTemplate,
    addonsTariffNos,
    startDate,
    deliveryArea,
  )

  const totalMonthlyFees = sumPricesByType(
    contractTemplate,
    contractTemplate.availableAddons.filter((addon) => addonsTariffNos.includes(addon.tariffNo)),
    startDate,
    deliveryArea,
    (type) => type === TARIFF_ELEMENT_TYPE_PREFIX.monthlyFee,
  )

  const totalEnergyPrice = sumPricesByType(
    contractTemplate,
    contractTemplate.availableAddons.filter((addon) => addonsTariffNos.includes(addon.tariffNo)),
    startDate,
    deliveryArea,
    // energy elements also contains elements that we don't want to include in the total price
    // calculation, so we need to filter them out
    (type) => !SEASON_ENERGY_PRICE_ELEMENTS.includes(type) && isEnergyElement(type),
  )

  const { monthlyFeeSavings, energyFeeSavings } = calculateFinnishMonthlySavings(
    addonsTariffNos,
    contractTemplate,
  )

  const energyTariffElement = contractTemplate.tariffElements.find((element) =>
    isEnergyElement(element.type),
  )

  const {
    energyPrice,
    discountedEnergyPrice,
    formattedEnergyPrice,
    energyPriceToDisplay,
    duration,
    isEnergyDiscounted,
  } = getEnergyPriceDetails({
    tariffElement: energyTariffElement,
    contractTemplate,
    formatPriceWithUnit,
  })

  const energyPriceExtraText = useContractEnergyPriceExtraText(contractTemplate)
  const hourlyPriceExtraText = energyPriceExtraText
    ? `${energyPriceExtraText?.text} ${energyPriceExtraText?.sign} `
    : undefined

  return (
    <Box display="flex" flexDirection="column" gap={spacing.xxs}>
      <Box display="flex" justifyContent="space-between" flexWrap="nowrap" gap={spacing.xxxxs}>
        <ContentText color={theme.colors.textPrimary} style={{ hyphens: 'auto' }}>
          {t('priceEnergyTitle')}
        </ContentText>
        <ContentText size="s" weight="medium" textAlign="right">
          {contractTemplate.priceType === 'SPOT' && hourlyPriceExtraText}
          {energyPriceToDisplay}
        </ContentText>
      </Box>

      {isEnergyDiscounted && (
        <Box
          display="flex"
          alignItems="flex-end"
          flexDirection="column"
          flexWrap="nowrap"
          marginTop={`-${px2rem(spacing.xxs)}`} //  negate the gap set by parent element
        >
          <CrossedOutText
            tag="span"
            display="flex"
            mt={spacing.xxxxs}
            aria-label={tAriaLabels('originalPrice')}
            color={colors.textSecondary}
            fontSize={fontSizes.xs}
          >
            {formattedEnergyPrice}
          </CrossedOutText>
          <ContentText
            tag="span"
            fontSize={fontSizes.xs}
            color={colors.textPositive}
            textWrap="word"
          >
            {t('discount', {
              discountDuration: duration?.amount ?? 'none',
              discountDurationType: duration?.type || 'MONTH',
              discountType: 'energy',
              discountPercent: discountPercentToString(
                energyPrice,
                discountedEnergyPrice,
                formatPrice,
              ),
            })}
          </ContentText>
        </Box>
      )}

      <Divider
        color={{ line: theme.colors.borderPrimary }}
        variant="dashed"
        verticalSpacing="xxxxs"
      />

      <MonthlyFeeRows
        monthlyFeeElements={monthlyFeeElements}
        energyElements={energyElements}
        contractTemplate={contractTemplate}
      />

      {startupCostToDisplay && (
        <>
          <Divider
            color={{ line: theme.colors.borderPrimary }}
            variant="dashed"
            verticalSpacing="xxxxs"
          />

          <Box display="flex" justifyContent="space-between" flexWrap="nowrap" gap={spacing.xxxxs}>
            <ContentText color={theme.colors.textPrimary} style={{ hyphens: 'auto' }}>
              {t('startupFee')}
            </ContentText>
            <ContentText size="s" weight="medium" textAlign="right">
              {startupCostToDisplay}
            </ContentText>
          </Box>
        </>
      )}

      <Divider
        color={{ line: theme.colors.borderPrimary }}
        variant="solid"
        verticalSpacing="none"
        height={px2rem(2)}
      />

      <TotalPriceRow
        totalMonthlyFees={totalMonthlyFees}
        totalEnergyPrice={totalEnergyPrice}
        monthlyFeeDiscount={monthlyFeeSavings}
        energyFeeDiscount={energyFeeSavings}
        startupCostToDisplay={startupCostToDisplay}
        contractTemplate={contractTemplate}
      />

      {(monthlyFeeSavings.priceExclVat !== 0 || energyFeeSavings.priceExclVat !== 0) && (
        <>
          <Divider
            color={{ line: theme.colors.borderPrimary }}
            variant="dashed"
            verticalSpacing="xxxxs"
          />
          <MonthlySavings
            monthlyFeeSavings={monthlyFeeSavings}
            energyFeeSavings={energyFeeSavings}
            customerType={contractTemplate.customerType}
          />
        </>
      )}
    </Box>
  )
}

const CrossedOutText = styled(ContentText)`
  text-decoration: line-through;
`
